import { ApplicationRef, Injectable, inject } from "@angular/core";
import {
    AbstractColDef,
    ColDef,
    ColGroupDef,
    Column,
    GridApi,
    GridOptions,
    ICellEditorParams,
    ICellRendererParams
} from "ag-grid-community";
import { Subject } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { GridButtonComponent } from "./components/grid-button/grid-button.component";
import { GridCheckboxComponent } from "./components/grid-checkbox/grid-checkbox.component";
import { GridInputEditorComponent } from "./components/grid-input-editor/grid-input-editor.component";
import { GridInputNumberComponent } from "./components/grid-input-number/grid-input-number.component";
import { GridLinkComponent } from "./components/grid-link/grid-link.component";
import { GridMultiSelectEditorComponent } from "./components/grid-multi-select-editor/grid-multi-select-editor.component";
import { GridSelectorEditorComponent } from "./components/grid-selector-editor/grid-selector-editor.component";
import { GridSpanComponent } from "./components/grid-span/grid-span.component";
import { GridSrefComponent } from "./components/grid-sref/grid-sref.component";
import { GridTreeSeletorEditorComponent } from "./components/grid-tree-seletor-editor/grid-tree-seletor-editor.component";
import { GridHeaderComponent } from "./header/grid-header.component";
import { RaskColumn } from "./models/rask-column";
import { RaskColumnParam } from "./models/rask-column-param";
import { RaskDataChanged } from "./models/rask-data-changed";
import { RaskGridHeaderParams } from "./models/rask-grid-header-params";
@Injectable({
    providedIn: "root"
})
export class AgGridTableService {
    private readonly aRef = inject(ApplicationRef);

    getDefaultOptions(showColumns: boolean): GridOptions {
        return {
            rowSelection: {
                mode: "singleRow",
                checkboxes: false,
                enableClickSelection: true
            },
            suppressMovableColumns: true,
            suppressMenuHide: true,
            suppressRowTransform: true,
            headerHeight: showColumns ? 40 : 0,
            rowHeight: 36,
            domLayout: "autoHeight",
            onFilterChanged: this.filterChanged.bind(this),
            localeText: this.getTexts(),
            suppressNoRowsOverlay: true,
            rowDragManaged: true
        } as GridOptions;
    }

    getParentElement(element: HTMLElement, className: string) {
        let current = element;
        while (current !== undefined && !current.classList.contains(className)) {
            current = current.parentElement;
        }
        return current;
    }

    handleKey(e: KeyboardEvent, gridApi: GridApi) {
        if (e.shiftKey && e.key === "Tab") {
            return this.nextCell(e.target, gridApi, false);
        } else if (e.key === "Tab") {
            return this.nextCell(e.target, gridApi);
        }
        return false;
    }

    nextCell(current: any, gridApi: GridApi, forward = true) {

        const columns = gridApi.getColumns().map(c => c.getColId());
        const gridCell = this.getParentElement(current, "ag-cell");
        const currentColName = gridCell.getAttribute("col-id");
        const pos = columns.indexOf(currentColName);

        if (pos === 0 && !forward || pos === (columns.length - 1) && forward) {
            return false;
        }

        const nextPos = forward ? pos + 1 : pos - 1;
        const nextCol = columns[nextPos];

        const nextCell = this.getCellWithColId(Array.from(gridCell.parentElement.getElementsByClassName("ag-cell")), nextCol);
        const input = nextCell.getElementsByTagName("input")[0];

        if (input === undefined || input === null) {
            return false;
        }
        setTimeout(() => {
            input.focus();
        }, 100);
        return true;
    }

    getCellWithColId(cells: Element[], colId: string) {
        for (const cell of cells) {
            if (cell.getAttribute("col-id") === colId) {
                return cell;
            }
        }
        return undefined;
    }

    filterChanged(p) {
        this.aRef.tick();
    }

    getColDefsFromRaskColumn(columns: RaskColumn[]): AbstractColDef[] {
        const tableDataChanged = new Subject();
        const result: (ColDef | ColGroupDef)[] = [];
        columns.forEach(c => {
            if (ObjectUtils.hasValue(c.children)) {
                const colGroupDef: ColGroupDef = {
                    headerName: c.headerName,
                    children: c.children.map(x => this.createColDef(x, tableDataChanged, true))
                };
                this.setTooltipComponent(colGroupDef, c.tooltipComponent);
                result.push(colGroupDef);
            } else {
                const colDef = this.createColDef(c, tableDataChanged, false);
                this.setCellClass(colDef);
                this.setTooltipComponent(colDef, c.tooltipComponent);
                result.push(colDef);
            }
        });
        return result;
    }

    handleValueChange(params: any, column: RaskColumn, value: any, change: Subject<any>) {
        params.data[column.field] = value;
        const changed = new RaskDataChanged(params.data, column.field, value);
        change.next(changed);
        column.dataChanged.next(changed);
    }

    getColDefsFromObject(object: any[], editable = false): ColDef[] {
        const result = [] as ColDef[];
        const row = object[0];
        for (const key in row) {
            if (Object.hasOwn(row, key) && !key.startsWith("_")) {
                const col = {
                    field: key,
                    editable: editable,
                    sortable: true
                } as ColDef;
                this.setCellClass(col);
                result.push(col);
            }
        }
        return result;
    }

    setTooltipComponent(col: AbstractColDef, component: string) {
        if (ObjectUtils.stringHasValue(component)) {
            col.tooltipComponent = component;
            col.headerTooltip = "-";
            if (ObjectUtils.hasValue((col as any).tooltipField)) {
                (col as any).tooltipField = "-";
            }
        }
    }

    setCellClass(col: ColDef) {
        const list = ["cell-default"];
        if (col.editable) {
            list.push("cell-editable");
        }
        if (col.cellRenderer) {
            list.push("cell-full");
        }
        col.cellClass = list;
    }

    getCellComponent(col: RaskColumn, edit: boolean) {
        switch (col.type) {
            case "number": {
                return GridInputNumberComponent;
            }
            case "selector": {
                return edit ? GridSelectorEditorComponent : GridSpanComponent;
            }
            case "checkbox": {
                return GridCheckboxComponent;
            }
            case "button": {
                return GridButtonComponent;
            }
            case "treeselector": {
                return edit ? GridTreeSeletorEditorComponent : GridSpanComponent;
            }
            case "link": {
                return GridLinkComponent;
            }
            case "sref": {
                return GridSrefComponent;
            }
            case "span": {
                return GridSpanComponent;
            }
            case "multiselector": {
                return edit ? GridMultiSelectEditorComponent : GridSpanComponent;
            }
            default: {
                return edit ? GridInputEditorComponent : GridSpanComponent;
            }
        }
    }

    syncColumnsWidth(gridColumns: Column[], raskColumns: RaskColumn[]) {
        if (!ObjectUtils.hasValue(raskColumns) || !ObjectUtils.hasValue(gridColumns)) {
            return;
        }
        gridColumns.forEach(c => {
            const raskColumn = raskColumns.find(r => r.field === c.getColId());
            if (ObjectUtils.hasValue(raskColumn)) {
                raskColumn.renderedWidth = c.getActualWidth();
            }
        });
    }

    getTexts() {
        return {
            // for number filter and text filter
            filterOoo: "Filter...",
            equals: "Lika med",
            notEqual: "Inte lika med",

            // for text filter
            contains: "Innehåller",
            notContains: "Innehåller inte",
            startsWith: "Börjar med",
            endsWith: "Slutar med",

            // filter conditions
            andCondition: "OCH",
            orCondition: "ELLER",

            // filter buttons
            applyFilter: "Applicera filter",
            resetFilter: "Återställ",
            clearFilter: "Rensa"
        };
    }

    private createColDef(c: RaskColumn, tableDataChanged?: Subject<unknown>, isChild = false): ColDef {
        return {
            cellRenderer: this.getCellComponent(c, false),
            cellRendererParams: (params: ICellRendererParams) => ({
                column: c,
                dataChanged: tableDataChanged,
                model: params.data,
                value: params.value,
                tooltipText: params?.data?.tooltipText,
                valueChangedCallback: (v) => this.handleValueChange(params, c, v, tableDataChanged)
            } as RaskColumnParam),

            cellEditorParams: c.editable ? (params: ICellEditorParams) => ({
                column: c,
                dataChanged: tableDataChanged,
                model: params.data,
                value: params.value,
                tooltipText: params?.data?.tooltipText,
                valueChangedCallback: (v) => this.handleValueChange(params, c, v, tableDataChanged)
            } as RaskColumnParam) : null,
            cellEditor: c.editable ? this.getCellComponent(c, true) : null,
            headerComponent: GridHeaderComponent,
            headerComponentParams: (params: any) => {
                if (c.headerParams) {
                    params.iconClass = c.headerParams.iconClass;
                    params.popoverMessage = c.headerParams.popoverMessage;
                    params.tooltipText = c.headerParams.tooltipText;
                }
                params.enableMenu = c.filter; // Oklart varför den här behövar sättas efter uppgradering till ag 33

                return params as RaskGridHeaderParams;
            },
            editable: c.editable,
            field: c.field,
            filter: c.filter,
            filterParams: c.filterParams,
            headerName: c.headerName,
            width: c.width,
            initialWidth: c.initialWidth,
            maxWidth: c.maxWidth,
            minWidth: c.minWidth,
            sortable: c.sortable,
            comparator: c.sortComparator,
            valueFormatter: (a, b) => a, // Borde ses över hur den ska användas
            valueParser: (v) => v, // Borde ses över hur den ska användas
            headerClass: isChild === true ? "no-horizontal-border" : null,
            cellClass: isChild === true ? "no-horizontal-border" : null,
            cellClassRules: c.cellClassRules,
            cellStyle: c.cellStyle,
            resizable: c.resizable || false,
            hide: c.hide || false,
            rowDrag: c.rowDrag || false,
            suppressSpanHeaderHeight: true
        } as ColDef;
    }
}
